import { useState } from "react";
import { Info, ChevronUp } from "lucide-react";
import { LEVEL_NAMES } from "./ConnectionLevelModal";

export const EscalationProgressBar = ({
  escalationState,
  driftInfo,
  selectedDifficulty,
  currentAchievementDisplay,
  onShowLevelModal,
  onShowObjectiveSlideout,
  compactProgressMode,
  setCompactProgressMode,
}) => {
  const [showEscalationTooltip, setShowEscalationTooltip] = useState(false);
  const [showDebugPanel, setShowDebugPanel] = useState(false);

  if (!escalationState) return null;

  return (
    <>
      {/* Compact Circular Progress Indicator - Always visible, acts as toggle */}
      <div 
        className="fixed top-[70px] left-4 z-50 cursor-pointer group flex flex-col items-center"
        onClick={() => {
          onShowLevelModal();
          setCompactProgressMode(!compactProgressMode);
        }}
        title={compactProgressMode ? "Click to expand progress" : "Click to collapse progress"}
      >
        {/* SVG Circular Progress with Drift Animation */}
        <svg 
          className={`w-14 h-14 -rotate-90 transform drop-shadow-lg ${
            driftInfo.drift_bonus === 1 ? "animate-pulse" :
            driftInfo.drift_bonus >= 2 ? "animate-[pulse_0.5s_ease-in-out_infinite]" : ""
          }`} 
          viewBox="0 0 48 48"
        >
          {/* Background circle with slight fill */}
          <circle
            cx="24"
            cy="24"
            r="22"
            fill="rgba(15,15,20,0.9)"
            stroke={
              driftInfo.drift_bonus >= 2 ? "rgba(147,51,234,0.4)" :
              driftInfo.drift_bonus === 1 ? "rgba(34,197,94,0.3)" :
              "rgba(255,255,255,0.15)"
            }
            strokeWidth="3"
            className="transition-colors duration-500"
          />
          {/* Progress arc */}
          <circle
            cx="24"
            cy="24"
            r="19"
            fill="none"
            stroke={
              driftInfo.drift_bonus >= 2 ? "#a855f7" :
              driftInfo.drift_bonus === 1 ? "#22c55e" :
              escalationState.trend === "up" ? "#10b981" :
              escalationState.trend === "down" ? "#f97316" :
              "#059669"
            }
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={`${(escalationState.xp_percentage / 100) * 119.4} 119.4`}
            className="transition-all duration-500"
          />
        </svg>
        {/* Level number in center */}
        <div className="absolute top-0 w-14 h-14 flex items-center justify-center">
          <span className={`font-bold text-xl transition-colors duration-500 ${
            driftInfo.drift_bonus >= 2 ? "text-purple-400" :
            driftInfo.drift_bonus === 1 ? "text-green-400" :
            "text-white"
          }`}>
            {escalationState.level}
          </span>
        </div>
        {/* Level name with drift indicator below circle */}
        <span className={`text-[10px] font-medium mt-1 transition-colors duration-500 ${
          driftInfo.drift_bonus >= 2 ? "text-purple-400" :
          driftInfo.drift_bonus === 1 ? "text-green-400" :
          "text-white/70"
        }`}>
          {escalationState.level_label || LEVEL_NAMES[escalationState.level]}
          {driftInfo.drift_bonus > 0 && (
            <span className="ml-1 font-bold">+{driftInfo.drift_bonus}</span>
          )}
        </span>
        {/* Processing indicator */}
        {escalationState.processing && (
          <div className="absolute -top-1 -right-1">
            <span className="text-yellow-400 text-xs animate-pulse">⏳</span>
          </div>
        )}
      </div>
      
      {/* Full Progress Bar Section - Only when expanded */}
      {!compactProgressMode && (
        <div className="border-b border-white/10 bg-divine-charcoal/95 backdrop-blur-xl sticky top-[57px] z-40 ml-20">
          <div className="max-w-4xl mx-auto px-6 py-1">
            {/* Escalation Game Progress */}
            <div>
              <div className="flex items-center justify-between mb-0.5">
                {/* Left: Connection Progress title with info icon */}
                <div className="flex items-center gap-2">
                  <span className="text-white/80 text-xs font-medium">Connection Progress</span>
                  {escalationState.processing && (
                    <span className="text-yellow-400 text-[10px] animate-pulse">⏳ Analyzing...</span>
                  )}
                  {/* Info Icon Tooltip */}
                  <div className="relative">
                    <button 
                      onMouseEnter={() => setShowEscalationTooltip(true)}
                      onMouseLeave={() => setShowEscalationTooltip(false)}
                      className="text-white/40 hover:text-white/60"
                    >
                      <Info className="w-3 h-3" />
                    </button>
                    {showEscalationTooltip && escalationState.last_reasons?.length > 0 && (
                      <div className="absolute left-0 top-5 z-50 bg-divine-charcoal/90 backdrop-blur-xl border border-white/20 rounded-lg p-3 min-w-[220px] shadow-xl">
                        <p className="text-white/80 text-xs font-medium mb-2">
                          {escalationState.trend === "up" ? "📈 Strengthening" : escalationState.trend === "down" ? "📉 Cooling" : "➡️ Stable"}
                        </p>
                        <ul className="text-white/60 text-xs space-y-1">
                          {escalationState.last_reasons.map((r, i) => (
                            <li key={i} className="flex items-start gap-1">
                              <span className="text-white/40">•</span>
                              <span>{r}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
                {/* Collapse button */}
                <button
                  onClick={() => setCompactProgressMode(true)}
                  className="text-white/40 hover:text-white/60"
                  title="Collapse to compact view"
                >
                  <ChevronUp className="w-3 h-3" />
                </button>
              </div>
              
              {/* Center: Highest In-Progress Achievement */}
              {currentAchievementDisplay && (
                <div className="flex items-center gap-1.5">
                  <span className="text-sm">{currentAchievementDisplay.icon}</span>
                  <span className="text-[10px] text-divine-gold/80">
                    {currentAchievementDisplay.name}
                  </span>
                  <span className="text-[10px] text-white/40">
                    {Math.round((currentAchievementDisplay.progress || 0) * 100)}%
                  </span>
                </div>
              )}
              
              {/* Right: Trend and Level - clickable to show modal */}
              <div className="flex items-center gap-2 justify-end -mt-4">
                <span className={`text-xs px-1.5 py-0.5 rounded ${
                  escalationState.trend === "up" ? "bg-green-500/20 text-green-400" :
                  escalationState.trend === "down" ? "bg-red-500/20 text-red-400" :
                  "bg-white/10 text-white/60"
                }`}>
                  {escalationState.trend === "up" ? "↑" : escalationState.trend === "down" ? "↓" : "→"}
                  {Math.abs(escalationState.last_xp_delta).toFixed(0)}
                </span>
                <span 
                  className={`font-semibold text-xs cursor-pointer hover:underline ${
                    driftInfo.drift_bonus >= 2 ? "text-purple-400" :
                    driftInfo.drift_bonus === 1 ? "text-green-400" :
                    "text-emerald-400"
                  }`}
                  onClick={onShowLevelModal}
                  title="Click to view all connection levels"
                >
                  Level {escalationState.level}: {escalationState.level_label}
                  {driftInfo.drift_bonus > 0 && (
                    <span className="ml-1 text-xs font-bold animate-pulse">+{driftInfo.drift_bonus}</span>
                  )}
                </span>
              </div>
            </div>
            
            {/* XP Progress Bar - pulses when drift is active, clickable to show modal */}
            <div 
              className={`w-full bg-white/10 rounded-full h-2.5 overflow-hidden cursor-pointer ${
                driftInfo.drift_bonus === 1 ? "animate-pulse" :
                driftInfo.drift_bonus >= 2 ? "animate-[pulse_0.5s_ease-in-out_infinite]" : ""
              }`}
              onClick={onShowLevelModal}
              title="Click to view all connection levels"
            >
              <div
                className={`h-2.5 rounded-full transition-all duration-500 ${
                  driftInfo.drift_bonus >= 2 ? "bg-gradient-to-r from-purple-500 to-purple-400" :
                  driftInfo.drift_bonus === 1 ? "bg-gradient-to-r from-green-500 to-green-400" :
                  escalationState.trend === "up" ? "bg-gradient-to-r from-emerald-500 to-emerald-400" :
                  escalationState.trend === "down" ? "bg-gradient-to-r from-orange-500 to-orange-400" :
                  "bg-gradient-to-r from-emerald-600 to-emerald-500"
                }`}
                style={{ width: `${escalationState.xp_percentage}%` }}
              />
            </div>
            
            {/* XP Info Row with Coach Feedback */}
            <div className="flex items-center justify-between mt-1">
              <span className="text-white/40 text-[10px]">XP: {escalationState.xp.toFixed(0)}/100</span>
              {/* Coach Feedback - Centered */}
              {escalationState.coach_feedback && (
                <span className="text-white/50 text-[10px] italic">{escalationState.coach_feedback}</span>
              )}
              <div className="flex items-center gap-2">
                <span className="text-white/40 text-[10px]">
                  {escalationState.level < 13 ? `Next: ${LEVEL_NAMES[escalationState.level + 1]}` : "Max Level"}
                </span>
                {/* Escalation Guide Button */}
                <button
                  onClick={onShowObjectiveSlideout}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-divine-gold/20 text-divine-gold hover:bg-divine-gold/30"
                  data-testid="open-escalation-guide"
                >
                  🏹 Guide
                </button>
                {/* Debug Toggle */}
                <button
                  onClick={() => setShowDebugPanel(!showDebugPanel)}
                  className={`text-[10px] px-1.5 py-0.5 rounded ${
                    showDebugPanel ? "bg-purple-500/30 text-purple-300" : "bg-white/10 text-white/40 hover:text-white/60"
                  }`}
                >
                  🔧 Debug
                </button>
              </div>
            </div>
            
            {/* Debug Panel */}
            {showDebugPanel && (
              <DebugPanel 
                escalationState={escalationState} 
                selectedDifficulty={selectedDifficulty} 
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

// Debug Panel sub-component
const DebugPanel = ({ escalationState, selectedDifficulty }) => {
  const currentDifficulty = selectedDifficulty || escalationState?.debug?.difficulty || "medium";
  const difficultyBonus = { easy: 3, medium: 1, hard: 0 }[currentDifficulty] ?? 1;
  
  const DIFFICULTY_DEFAULTS = {
    easy: { upThresh: 1.0, downThresh: -5.0, upNeeded: 1, downNeeded: 3, overflow: 105, underflow: -15 },
    medium: { upThresh: 2.0, downThresh: -3.0, upNeeded: 2, downNeeded: 2, overflow: 110, underflow: -10 },
    hard: { upThresh: 3.5, downThresh: -2.0, upNeeded: 3, downNeeded: 2, overflow: 120, underflow: -5 },
  };
  const defaults = DIFFICULTY_DEFAULTS[currentDifficulty] || DIFFICULTY_DEFAULTS.medium;
  const streakInfo = escalationState?.debug?.streak_info;
  const levelUpCheck = escalationState?.debug?.level_up_check;
  const levelDownCheck = escalationState?.debug?.level_down_check;

  return (
    <div className="mt-3 p-3 bg-black/40 border border-purple-500/30 rounded-lg text-xs font-mono">
      <div className="text-purple-400 font-bold mb-2">🔧 XP Debug Panel</div>
      
      {/* Difficulty Rating Banner */}
      <div className={`mb-3 p-2 rounded border ${
        currentDifficulty === 'easy' ? 'bg-green-500/20 border-green-500/40' :
        currentDifficulty === 'hard' ? 'bg-red-500/20 border-red-500/40' :
        'bg-yellow-500/20 border-yellow-500/40'
      }`}>
        <div className="flex items-center justify-between mb-2">
          <span className={`font-bold text-sm ${
            currentDifficulty === 'easy' ? 'text-green-400' :
            currentDifficulty === 'hard' ? 'text-red-400' :
            'text-yellow-400'
          }`}>
            {currentDifficulty === 'easy' ? '🟢 EASY' :
             currentDifficulty === 'hard' ? '🔴 HARD' :
             '🟡 MEDIUM'} MODE
          </span>
          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
            currentDifficulty === 'easy' ? 'bg-green-500/30 text-green-300' :
            currentDifficulty === 'hard' ? 'bg-red-500/30 text-red-300' :
            'bg-yellow-500/30 text-yellow-300'
          }`}>
            +{difficultyBonus} XP/turn
          </span>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
          <div className="flex justify-between">
            <span className="text-white/50">Delta for ↑streak:</span>
            <span className="text-white/80">&gt; {streakInfo?.upstreak_threshold ?? defaults.upThresh}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/50">Delta for ↓streak:</span>
            <span className="text-white/80">&lt; {streakInfo?.downstreak_threshold ?? defaults.downThresh}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/50">↑streaks to level up:</span>
            <span className="text-white/80">{streakInfo?.needs_upstreak_for_levelup ?? defaults.upNeeded}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/50">↓streaks to level down:</span>
            <span className="text-white/80">{streakInfo?.needs_downstreak_for_leveldown ?? defaults.downNeeded}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/50">Overflow threshold:</span>
            <span className="text-white/80">≥{levelUpCheck?.overflow_threshold ?? defaults.overflow}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/50">Underflow threshold:</span>
            <span className="text-white/80">≤{levelDownCheck?.underflow_threshold ?? defaults.underflow}</span>
          </div>
        </div>
      </div>
      
      {/* Streak Status - only show if we have debug data */}
      {escalationState?.debug && (
        <div className="mb-3 p-2 bg-purple-500/10 rounded">
          <div className="text-purple-300 font-semibold mb-1">Streak Status</div>
          <div className="grid grid-cols-2 gap-2 text-[10px]">
            <div>
              <span className="text-white/50">Upstreak: </span>
              <span className={streakInfo?.current_upstreak >= (streakInfo?.needs_upstreak_for_levelup || 2) ? "text-green-400" : "text-yellow-400"}>
                {streakInfo?.current_upstreak || 0}/{streakInfo?.needs_upstreak_for_levelup || 2}
              </span>
              {streakInfo?.current_upstreak >= (streakInfo?.needs_upstreak_for_levelup || 2) && " ✅"}
            </div>
            <div>
              <span className="text-white/50">Downstreak: </span>
              <span className={streakInfo?.current_downstreak >= (streakInfo?.needs_downstreak_for_leveldown || 2) ? "text-red-400" : "text-white/70"}>
                {streakInfo?.current_downstreak || 0}/{streakInfo?.needs_downstreak_for_leveldown || 2}
              </span>
            </div>
            <div>
              <span className="text-white/50">Up threshold: </span>
              <span className="text-white/70">&gt; {streakInfo?.upstreak_threshold || 3}</span>
            </div>
            <div>
              <span className="text-white/50">Down threshold: </span>
              <span className="text-white/70">&lt; {streakInfo?.downstreak_threshold || -3}</span>
            </div>
            <div className="col-span-2">
              <span className="text-white/50">Last qualifies for upstreak: </span>
              <span className={streakInfo?.delta_qualifies_for_upstreak ? "text-green-400" : "text-red-400"}>
                {streakInfo?.delta_qualifies_for_upstreak ? "YES ✅" : "NO ❌"}
              </span>
            </div>
          </div>
        </div>
      )}
      
      {/* XP Breakdown - only show if we have debug data */}
      {escalationState?.debug && (
        <div className="mb-3 p-2 bg-blue-500/10 rounded">
          <div className="text-blue-300 font-semibold mb-1">XP Breakdown</div>
          <div className="grid grid-cols-2 gap-2 text-[10px]">
            <div>
              <span className="text-white/50">Raw Delta: </span>
              <span className={escalationState?.debug?.xp_breakdown?.raw_delta > 0 ? "text-green-400" : "text-red-400"}>
                {escalationState?.debug?.xp_breakdown?.raw_delta?.toFixed(2) || 0}
              </span>
            </div>
            <div>
              <span className="text-white/50">Smoothed: </span>
              <span className={escalationState?.debug?.xp_breakdown?.smoothed_delta > (streakInfo?.upstreak_threshold || 3) ? "text-green-400" : escalationState?.debug?.xp_breakdown?.smoothed_delta > 0 ? "text-yellow-400" : "text-red-400"}>
                {escalationState?.debug?.xp_breakdown?.smoothed_delta?.toFixed(2) || 0}
              </span>
              {escalationState?.debug?.xp_breakdown?.smoothed_delta > (streakInfo?.upstreak_threshold || 3) && " (Upstreak!)"}
            </div>
            <div>
              <span className="text-white/50">Previous: </span>
              <span className="text-white/70">{escalationState?.debug?.xp_breakdown?.previous_delta?.toFixed(2) || 0}</span>
            </div>
            <div>
              <span className="text-white/50">Difficulty: </span>
              <span className={`font-bold ${currentDifficulty === 'easy' ? 'text-green-400' : currentDifficulty === 'hard' ? 'text-red-400' : 'text-yellow-400'}`}>
                {currentDifficulty?.toUpperCase()} (+{difficultyBonus})
              </span>
            </div>
          </div>
          {escalationState?.debug?.xp_breakdown?.reasons_up?.length > 0 && (
            <div className="mt-2 text-[10px]">
              <span className="text-green-400">↑ </span>
              <span className="text-white/60">{escalationState?.debug?.xp_breakdown?.reasons_up?.join(", ")}</span>
            </div>
          )}
          {escalationState?.debug?.xp_breakdown?.reasons_down?.length > 0 && (
            <div className="mt-1 text-[10px]">
              <span className="text-red-400">↓ </span>
              <span className="text-white/60">{escalationState?.debug?.xp_breakdown?.reasons_down?.join(", ")}</span>
            </div>
          )}
        </div>
      )}
      
      {/* Signal Values - only show if we have debug data */}
      {escalationState?.debug?.signals && (
        <div className="mb-3 p-2 bg-cyan-500/10 rounded">
          <div className="text-cyan-300 font-semibold mb-1">Signal Values (0.0 - 1.0)</div>
          <div className="grid grid-cols-2 gap-1 text-[10px]">
            {Object.entries(escalationState.debug.signals).map(([key, value]) => {
              const weight = escalationState?.debug?.stage_weights?.[key] || 0;
              const isNegative = weight < 0;
              const contribution = isNegative 
                ? (value * 50 * weight).toFixed(1)
                : ((value - 0.5) * 50 * weight).toFixed(1);
              return (
                <div key={key} className="flex justify-between">
                  <span className="text-white/50 truncate" style={{maxWidth: '100px'}}>{key}:</span>
                  <span className={`${value > 0.6 ? "text-green-400" : value < 0.4 ? "text-red-400" : "text-white/70"}`}>
                    {typeof value === 'number' ? value.toFixed(2) : value}
                    <span className={`ml-1 ${parseFloat(contribution) > 0 ? "text-green-500" : parseFloat(contribution) < 0 ? "text-red-500" : "text-white/30"}`}>
                      ({contribution > 0 ? "+" : ""}{contribution})
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Level Up Check - only show if we have debug data */}
      {escalationState?.debug?.level_up_check && (
        <div className="p-2 bg-yellow-500/10 rounded">
          <div className="text-yellow-300 font-semibold mb-1">Level Up Check</div>
          <div className="grid grid-cols-2 gap-2 text-[10px]">
            <div>
              <span className="text-white/50">XP at 100: </span>
              <span className={levelUpCheck?.xp_at_100 ? "text-green-400" : "text-white/70"}>
                {levelUpCheck?.xp_at_100 ? "YES ✅" : "NO"}
              </span>
            </div>
            <div>
              <span className="text-white/50">Upstreak ≥{streakInfo?.needs_upstreak_for_levelup || 2}: </span>
              <span className={levelUpCheck?.upstreak_met ? "text-green-400" : "text-white/70"}>
                {levelUpCheck?.upstreak_met ? "YES ✅" : "NO"}
              </span>
            </div>
            <div>
              <span className="text-white/50">Overflow ≥{levelUpCheck?.overflow_threshold || 110}: </span>
              <span className={levelUpCheck?.overflow_met ? "text-green-400" : "text-white/70"}>
                {levelUpCheck?.overflow_met ? "YES ✅" : "NO"}
              </span>
            </div>
            <div>
              <span className="text-white/50">Would Level Up: </span>
              <span className={levelUpCheck?.would_level_up ? "text-green-400 font-bold" : "text-red-400"}>
                {levelUpCheck?.would_level_up ? "YES! 🎉" : "NO"}
              </span>
            </div>
          </div>
        </div>
      )}
      
      {/* No debug data yet message */}
      {!escalationState?.debug && (
        <div className="p-2 bg-white/5 rounded text-white/50 text-center text-xs">
          Send a message to see detailed XP analytics
        </div>
      )}
    </div>
  );
};

export default EscalationProgressBar;
